import { bunja } from 'bunja';
import { atom, type Atom } from 'jotai';
import { selectAtom } from 'jotai/utils';

import { OrderTypeEnum } from '@flipster/apis/types';
import { getPendingOrderRoles } from '@flipster/features/src/shared/position/pending-order-roles';
import { marketsBunja } from '@flipster/r230522/original/market';
import { ordersBunja, type PendingPosition } from '@flipster/r230522/original/order';
import { orderbooksBunja } from '@flipster/r230522/original/orderbooks';
import { spotOrdersBunja, type SpotPendingPosition } from '@flipster/r230522/original/spot-order';
import { atomWithRafThrottle } from '@flipster/react/misc/state/atom-with-raf-throttle';
import { UsdtCurrency } from '@flipster/utils/constants/currency';

import { orderbookStateBunja } from '@web/app/[locale]/(web)/trade/_misc/state';

import { symbolDisplayPriceBunja, tradeSymbolBunja } from '../symbol';
import type { OrderbookItem } from './OrderbookRow';
import { aggregateOrderbook } from './aggregate-orderbook';
import {
  AggregatedOrderbookLevel,
  computeBidAskRatio,
  mergeStableOrderbookItems,
  orderbookMaxItems,
  processAndNormalizeOrderbookData,
} from './orderbook-items';

type LimitPendingPosition = PendingPosition | SpotPendingPosition;

// row 마킹 비교는 price/side만 사용한다. 캐스팅 없이 유니온 타입 그대로 다뤄
// 다른 필드를 참조하면 컴파일 에러가 나게 한다. (price는 perp·spot 둘 다 string이라 === 값 비교가 안정적 —
//  기존의 Array<{price?: string; side}> 캐스팅은 이 사실을 표기했을 뿐 런타임 비교엔 영향 없었다)
const isSameMark = (a: LimitPendingPosition, b: LimitPendingPosition) => a.side === b.side && a.price === b.price;
const isSameMarks = (a: readonly LimitPendingPosition[], b: readonly LimitPendingPosition[]) =>
  a.length === b.length && a.every((p, i) => isSameMark(p, b[i]));

/**
 * 오더북 표시 전용 데이터 레이어. 계산은 전부 여기서 하고 UI는 consume만 한다.
 * 소켓 tick을 프레임당 1회(requestAnimationFrame)로 병합해 리렌더 빈도를 프레임 레이트로 캡핑한다.
 * 표시값이 최대 1프레임 지연되므로 주문 제출·검증 경로에서는 사용하지 않는다.
 */
export const orderbookDataBunja = bunja(() => {
  const {
    symbol,
    isSpotAtom,
    quoteCurrencyAtom: symbolQuoteCurrencyAtom,
    baseCurrencyAtom: symbolBaseCurrencyAtom,
    positionUnitAtom,
  } = bunja.use(tradeSymbolBunja);
  const { orderbooksAtom } = bunja.use(orderbooksBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const { throttledCenterDisplayPriceAtom } = bunja.use(symbolDisplayPriceBunja);
  const { pendingPositionsAtom: perpPendingPositionsAtom } = bunja.use(ordersBunja);
  const { pendingPositionsAtom: spotPendingPositionsAtom } = bunja.use(spotOrdersBunja);
  const { selectedTickSizeAtom, baseTickSizeAtom } = bunja.use(orderbookStateBunja);

  // --- 소스: raf throttle로 tick을 프레임 단위로 병합 ---
  const throttledOrderbookAtom = atomWithRafThrottle(atom((get) => get(orderbooksAtom)?.[symbol]));
  const throttledPerpMarketAtom = atomWithRafThrottle(atom((get) => get(marketsAtom)?.[symbol]));

  // --- 가격 (string이라 값이 같으면 Object.is dedup으로 전파가 끊긴다) ---
  // 중앙가: perp last / spot mid. throttledCenterDisplayPriceAtom은 symbol.ts에서 이미 RAF 스로틀됨.
  const centerPriceAtom = atom((get) => get(throttledCenterDisplayPriceAtom) ?? '0');
  // spot에는 mark price가 없다
  const markPriceAtom = atom((get) => (get(isSpotAtom) ? undefined : get(throttledPerpMarketAtom)?.markPrice || '0'));

  // --- 헤더 라벨 표시 단위 (symbolInfo 객체 통구독 대신 문자열 값만 전파) ---
  const quoteCurrencyAtom = atom((get) => get(symbolQuoteCurrencyAtom) ?? UsdtCurrency);
  // perp의 수량 단위는 positionUnit (spot은 baseCurrency)
  const baseCurrencyAtom = atom((get) => (get(isSpotAtom) ? get(symbolBaseCurrencyAtom) : get(positionUnitAtom)));

  // --- tick size 버킷 집계 ---
  const aggregatedAsksAtom = atom((get) =>
    aggregateOrderbook(
      get(throttledOrderbookAtom)?.asks ?? [],
      'ask',
      get(selectedTickSizeAtom),
      get(baseTickSizeAtom),
    ),
  );
  const aggregatedBidsAtom = atom((get) =>
    aggregateOrderbook(
      get(throttledOrderbookAtom)?.bids ?? [],
      'bid',
      get(selectedTickSizeAtom),
      get(baseTickSizeAtom),
    ),
  );

  // 표시용 row 아이템. 값이 같은 슬롯은 이전 객체를 재사용해 OrderbookRow memo가 동작하게 한다.
  // 이전 slice는 selectAtom이 store별로 보관하므로(read 함수는 순수) 클로저 mutation 없이 diff-and-reuse가 된다.
  // mergeStableOrderbookItems가 전부 같을 땐 prev를 그대로 반환 → 기본 Object.is equalityFn으로 배열 전파까지 차단된다.
  const createStableItemsAtom = (aggregatedAtom: Atom<AggregatedOrderbookLevel[]>): Atom<OrderbookItem[]> =>
    selectAtom(
      atom((get) => processAndNormalizeOrderbookData(get(aggregatedAtom), orderbookMaxItems)),
      (next, prev) => (prev ? mergeStableOrderbookItems(prev, next) : next),
    );
  const askItemsAtom = createStableItemsAtom(aggregatedAsksAtom);
  const bidItemsAtom = createStableItemsAtom(aggregatedBidsAtom);

  // volume bar 비율의 분모 (누적 sum의 최대값)
  const maxAskSizeAtom = atom((get) => Math.max(...get(askItemsAtom).map((item) => item.sum?.toNumber() || 0)));
  const maxBidSizeAtom = atom((get) => Math.max(...get(bidItemsAtom).map((item) => item.sum?.toNumber() || 0)));

  // 패딩 아닌 실제 레벨 수 (스크롤 중앙 정렬 계산에 사용)
  const realAskCountAtom = atom((get) => get(askItemsAtom).filter((item) => item.price !== null).length);
  const realBidCountAtom = atom((get) => get(bidItemsAtom).filter((item) => item.price !== null).length);

  const bidAskRatioAtom = selectAtom(
    atom((get) => computeBidAskRatio(get(aggregatedAsksAtom), get(aggregatedBidsAtom))),
    (ratio) => ratio,
    (a, b) => a.bidPercent === b.bidPercent && a.isEmpty === b.isEmpty,
  );

  // 오더북 row에 dot으로 마킹할 limit 대기 주문.
  // row 마킹은 price/side만 사용하므로, 그 키가 전부 같으면 selectAtom이 이전 배열 identity를 유지해
  // OrderbookRows memo를 보호한다. 이전 slice는 selectAtom이 store별로 보관 → read 함수는 순수.
  const limitPendingPositionsAtom = selectAtom(
    atom<PendingPosition[] | SpotPendingPosition[]>(
      (get) =>
        (get(isSpotAtom)
          ? get(spotPendingPositionsAtom)?.[symbol]?.filter((p) => p.orderType === OrderTypeEnum['Limit'])
          : get(perpPendingPositionsAtom)?.[symbol]?.filter((p) => getPendingOrderRoles(p).isLimitType)) || [],
    ),
    (positions) => positions,
    isSameMarks,
  );

  return {
    centerPriceAtom,
    markPriceAtom,
    quoteCurrencyAtom,
    baseCurrencyAtom,
    askItemsAtom,
    bidItemsAtom,
    maxAskSizeAtom,
    maxBidSizeAtom,
    realAskCountAtom,
    realBidCountAtom,
    bidAskRatioAtom,
    limitPendingPositionsAtom,
  };
});
orderbookDataBunja.debugLabel = 'orderbookDataBunja';
